import classNames from "clsx"
import { motion } from "framer-motion"
import { createContext, type ReactNode, useContext, useEffect, useMemo, useState, useCallback, useRef } from "react"
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom"
import { useAuth, useClerk } from "@clerk/clerk-react"
import { useStatusHealth, useTenant } from "./hooks/useApi"
import { useTheme } from "./theme/ThemeProvider"
import { useDemoMode } from "./demo/DemoProvider"
import { ThemeToggle } from "./components/ThemeToggle"
import { Rail } from "./components/Rail"
import { Drawer } from "./components/Drawer"
import { useBreakpoint } from "./hooks/useMediaQuery"
import { LAST_CONTACT_EVENT, LAST_CONTACT_STORAGE_KEY } from "./constants/storage"
import { trackDegradedRails } from "./utils/telemetry"
import { apiFetch } from "./api/http"

import type { Tenant } from "./types"

const IS_STAGING = import.meta.env.VITE_API_URL?.includes("staging") || import.meta.env.MODE === "development"

type StagingResetResponse = {
  status: string
  snapshot: Record<string, unknown>
  reset_actions: string[]
}

const ENABLE_ADMIN = (import.meta.env.VITE_ENABLE_ADMIN_CONSOLE as string | undefined) === "true"

type ControlCenterContextValue = {
  tenant: Tenant
}

const ControlCenterContext = createContext<ControlCenterContextValue | undefined>(undefined)

type DetailPaneState = {
  ownerId: string | null
  open: boolean
  title?: ReactNode
  actions?: ReactNode
  content?: ReactNode
  onClose?: (() => void) | null
}

type DetailPaneContextValue = {
  state: DetailPaneState
  open: (ownerId: string, payload: Omit<DetailPaneState, "ownerId" | "open">) => void
  release: (ownerId: string) => void
  forceClose: () => void
}

const DetailPaneContext = createContext<DetailPaneContextValue | undefined>(undefined)

export function useControlCenter() {
  const ctx = useContext(ControlCenterContext)
  if (!ctx) {
    throw new Error("useControlCenter must be used within AppShell")
  }
  return ctx
}

export function useDetailPane() {
  const ctx = useContext(DetailPaneContext)
  if (!ctx) {
    throw new Error("useDetailPane must be used within AppShell")
  }
  return ctx
}

export function AppShell() {
  const { data: tenant, isLoading, error } = useTenant()
  const statusHealth = useStatusHealth()
  const { theme } = useTheme()
  const { demo, toggleDemo } = useDemoMode()
  const { signOut } = useClerk()
  const { getToken } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const isDesktop = useBreakpoint("md")
  const [isResetting, setIsResetting] = useState(false)
  const currentPath = typeof location?.pathname === "string" && location.pathname ? location.pathname : "/app"
  const [navOpen, setNavOpen] = useState(false)
  const [lastContactId, setLastContactId] = useState<string | null>(() => {
    if (typeof window === "undefined") return null
    const stored = window.localStorage.getItem(LAST_CONTACT_STORAGE_KEY)
    return stored && stored.trim() ? stored : null
  })
  const [detailPane, setDetailPane] = useState<DetailPaneState>({
    ownerId: null,
    open: false,
    title: undefined,
    actions: undefined,
    content: undefined,
    onClose: null,
  })
  const ignoreNextOpenRef = useRef(false)
  const degradedBanner = statusHealth.data?.degraded_banner
  const degradedRails = useMemo(() => degradedBanner?.rails ?? [], [degradedBanner?.rails])

  useEffect(() => {
    if (tenant) {
      ;(globalThis as typeof globalThis & { __currentTenant?: Tenant }).__currentTenant = tenant
    }
  }, [tenant])

  useEffect(() => {
    if (degradedBanner?.state === "degraded" && degradedRails.length) {
      trackDegradedRails(degradedRails, "appShell.header")
    }
  }, [degradedBanner?.state, degradedRails])

  const openDetailPane = useCallback(
    (ownerId: string, payload: Omit<DetailPaneState, "ownerId" | "open">) => {
      // Check if we should ignore this open request (set during force close)
      if (ignoreNextOpenRef.current) {
        ignoreNextOpenRef.current = false
        return
      }
      
      setDetailPane((prev) => {
        // If already open for the same owner with the same content references,
        // return the previous state to avoid unnecessary re-renders
        if (
          prev.open &&
          prev.ownerId === ownerId &&
          prev.title === payload.title &&
          prev.actions === payload.actions &&
          prev.content === payload.content &&
          prev.onClose === (payload.onClose ?? null)
        ) {
          return prev
        }

        // Update the detail pane with new content
        return {
          ownerId,
          open: true,
          title: payload.title,
          actions: payload.actions,
          content: payload.content,
          onClose: payload.onClose ?? null,
        }
      })
    },
    [],
  )

  const releaseDetailPane = useCallback((ownerId: string) => {
    setDetailPane((previous) => {
      if (previous.ownerId !== ownerId) {
        return previous
      }
      return {
        ownerId: null,
        open: false,
        title: undefined,
        actions: undefined,
        content: undefined,
        onClose: null,
      }
    })
  }, [])

  const forceCloseDetailPane = useCallback(() => {
    // Set flag to ignore the next open request (prevents re-opening race condition)
    ignoreNextOpenRef.current = true
    
    setDetailPane({
      ownerId: null,
      open: false,
      title: undefined,
      actions: undefined,
      content: undefined,
      onClose: null,
    })
    
    // Reset the flag after a brief delay to allow future opens
    setTimeout(() => {
      ignoreNextOpenRef.current = false
    }, 50)
  }, [])

  const personaLabel = theme === "fairy" ? "Fairy" : "Demon"
  const phonetic = theme === "fairy" ? "/ˈfɛə.ri/" : "/ˈdiː.mən/"
  const surfaceBg = theme === "fairy" ? "rgba(255,255,255,0.96)" : "rgba(18,18,20,0.88)"
  const railGlow = theme === "fairy" ? "0 0 12px rgba(46,214,181,0.4)" : "0 0 12px rgba(255,43,79,0.65)"
  const headerShadow = theme === "fairy" ? "0 18px 32px rgba(46,214,181,0.12)" : "0 12px 24px rgba(255,43,79,0.18)"
  const controlBackground = theme === "fairy" ? "rgba(244,255,251,0.92)" : "transparent"
  const controlText = theme === "fairy" ? "#1f2d42" : "var(--text)"
  const controlShadow = theme === "fairy" ? "0 10px 24px rgba(46,214,181,0.18)" : "none"
  const navTabs = useMemo(() => getTabsForTheme(theme), [theme])
  const isRouteActive = useCallback(
    (id: string) => {
      if (id === "/app/dashboard") {
        return (
          currentPath === "/app" ||
          currentPath === "/app/" ||
          currentPath.startsWith("/app/dashboard")
        )
      }
      return currentPath.startsWith(id)
    },
    [currentPath],
  )
  const detailPaneContext = useMemo<DetailPaneContextValue>(
    () => ({
      state: detailPane,
      open: openDetailPane,
      release: releaseDetailPane,
      forceClose: forceCloseDetailPane,
    }),
    [detailPane, openDetailPane, releaseDetailPane, forceCloseDetailPane],
  )

  const handleNavigate = (id: string) => {
    if (typeof id !== "string" || id.trim() === "") {
      return
    }
    navigate(id)
  }

  useEffect(() => {
    if (isDesktop && navOpen) {
      setNavOpen(false)
    }
  }, [isDesktop, navOpen])

  useEffect(() => {
    setNavOpen(false)
  }, [currentPath])

  // Auto-close detail pane when navigating to routes that don't support it
  useEffect(() => {
    const detailPaneSupportedRoutes = ["/app/invoices", "/app/customers"]
    const isSupported = detailPaneSupportedRoutes.some((route) => currentPath.startsWith(route))
    
    if (!isSupported && detailPane.open) {
      forceCloseDetailPane()
    }
  }, [currentPath, detailPane.open, forceCloseDetailPane])


  useEffect(() => {
    if (typeof window === "undefined") return
    const handleCustomEvent = (event: Event) => {
      const detail = (event as CustomEvent<string | null>).detail
      setLastContactId(detail && detail.trim() ? detail : null)
    }
    const handleStorage = (event: StorageEvent) => {
      if (event.key === LAST_CONTACT_STORAGE_KEY) {
        setLastContactId(event.newValue && event.newValue.trim() ? event.newValue : null)
      }
    }
    window.addEventListener(LAST_CONTACT_EVENT, handleCustomEvent as EventListener)
    window.addEventListener("storage", handleStorage)
    return () => {
      window.removeEventListener(LAST_CONTACT_EVENT, handleCustomEvent as EventListener)
      window.removeEventListener("storage", handleStorage)
    }
  }, [])

  const handleMessageClick = () => {
    if (lastContactId) {
      navigate(`/app/customers?contactId=${encodeURIComponent(lastContactId)}`)
    } else {
      navigate("/app/customers")
    }
  }

  const handleStagingReset = async () => {
    if (!IS_STAGING) return
    if (!window.confirm("STAGING RESET: This will delete all tenants, users, and data. Are you sure?")) return
    
    setIsResetting(true)
    try {
      const token = await getToken()
      if (!token) throw new Error("Admin authentication required")
      const response = await apiFetch<StagingResetResponse>("/api/staging/reset", {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      })
      
      // Log snapshot to console for debugging
      console.group("%c[STAGING RESET] Pre-reset state snapshot", "color: #ff2b4f; font-weight: bold; font-size: 14px;")
      console.table(response.snapshot)
      console.groupEnd()
      
      console.group("%c[STAGING RESET] Actions performed", "color: #ff2b4f; font-weight: bold; font-size: 14px;")
      response.reset_actions.forEach((action) => console.log(`  - ${action}`))
      console.groupEnd()
      
      // Clear local storage
      localStorage.clear()
      sessionStorage.clear()
      
      // Clear cookies
      document.cookie.split(";").forEach((cookie) => {
        const name = cookie.split("=")[0].trim()
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`
      })
      
      console.log("%c[STAGING RESET] Local storage, session storage, and cookies cleared", "color: #ff2b4f; font-weight: bold;")
      
      // Sign out from Clerk and redirect to home
      await signOut()
      window.location.href = "/"
    } catch (err) {
      console.error("[STAGING RESET] Failed:", err)
      alert("Reset failed - check console for details")
      setIsResetting(false)
    }
  }

  if (isLoading) {
    const loadingLabel = theme === "fairy" ? "Summoning your fairy…" : "Summoning your demon…"
    return <SceneWrapper>{loadingLabel}</SceneWrapper>
  }

  if (error || !tenant) {
    const failureLabel = "Couldn\u2019t load your dashboard. Refresh to retry."
    return <SceneWrapper>{failureLabel}</SceneWrapper>
  }

  return (
    <ControlCenterContext.Provider value={{ tenant }}>
      <DetailPaneContext.Provider value={detailPaneContext}>
        <div className="app-shell-frame">
          <div
            className={classNames("app-shell-container", {
              "app-shell-container--detail": detailPane.open && isDesktop,
            })}
          >
            <div className="app-shell">
              <Rail orientation="horizontal" style={{ boxShadow: railGlow }} />
              <header className="app-shell__header" style={{ background: surfaceBg, boxShadow: headerShadow }}>
                <div className="app-shell__menu">
                  {!isDesktop ? (
                    <button
                      type="button"
                      className="app-shell__menu-button"
                      aria-label="Open navigation menu"
                      aria-expanded={navOpen}
                      aria-controls="app-shell-nav"
                      onClick={() => setNavOpen(true)}
                    >
                      <span className="sr-only">Open menu</span>
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden>
                        <path d="M3 5h14M3 10h14M3 15h14" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                      </svg>
                    </button>
                  ) : null}
                  <img
                    src={theme === "fairy" ? "/assets/pixel/fairy_16.png" : "/assets/pixel/demon_16.png"}
                    alt="Persona sprite"
                    className="pixel"
                    style={{ width: 32, height: 32 }}
                  />
                  <div className="mono app-shell__brand-label">
                    <strong>{personaLabel.toUpperCase()}</strong>
                    <span>{phonetic}</span>
                  </div>
                </div>

                {demo ? (
                  <span className="mono app-shell__demo-pill" aria-label="Demo mode is on">
                    Demo
                  </span>
                ) : null}

                <div className="app-shell__actions">
                  <motion.button
                    type="button"
                    whileTap={tenant.payments_ready ? { scale: 0.95 } : undefined}
                    onClick={tenant.payments_ready ? handleMessageClick : undefined}
                    className="mono"
                    disabled={!tenant.payments_ready}
                    title={!tenant.payments_ready ? "Connect Stripe" : undefined}
                    aria-disabled={!tenant.payments_ready}
                    style={{
                      padding: "10px 16px",
                      border: tenant.payments_ready ? "2px solid var(--rail)" : "2px solid rgba(107,114,128,0.5)",
                      background: tenant.payments_ready ? controlBackground : "rgba(107,114,128,0.15)",
                      color: tenant.payments_ready ? controlText : "rgba(107,114,128,0.7)",
                      letterSpacing: "0.16em",
                      textTransform: "uppercase",
                      cursor: tenant.payments_ready ? "pointer" : "not-allowed",
                      boxShadow: tenant.payments_ready ? controlShadow : "none",
                      minHeight: "44px",
                      display: "inline-flex",
                      alignItems: "center",
                      opacity: tenant.payments_ready ? 1 : 0.65,
                    }}
                  >
                    Message
                  </motion.button>
                  <ThemeToggle />
                  {demo ? (
                  <motion.button
                    type="button"
                    onClick={toggleDemo}
                    whileTap={{ scale: 0.9 }}
                    className="mono"
                    style={{
                      padding: "8px 14px",
                      border: demo
                        ? "2px solid var(--accent-2)"
                        : theme === "fairy"
                          ? "2px solid var(--rail)"
                          : "2px solid rgba(255,43,79,0.5)",
                      background: demo ? "rgba(255,122,26,0.12)" : controlBackground,
                      letterSpacing: "0.16em",
                      textTransform: "uppercase",
                      cursor: "pointer",
                      color: demo ? "var(--accent-2)" : controlText,
                      boxShadow: demo ? "none" : controlShadow,
                      minHeight: "44px",
                      display: "inline-flex",
                      alignItems: "center",
                    }}
                  >
                    exit demo
                  </motion.button>
                  ) : null}
                  {IS_STAGING && ENABLE_ADMIN ? (
                    <motion.button
                      type="button"
                      onClick={handleStagingReset}
                      disabled={isResetting}
                      whileTap={{ scale: 0.9 }}
                      className="mono"
                      style={{
                        padding: "8px 14px",
                        border: "2px solid rgba(239,68,68,0.7)",
                        background: "rgba(239,68,68,0.15)",
                        letterSpacing: "0.16em",
                        textTransform: "uppercase",
                        cursor: isResetting ? "wait" : "pointer",
                        color: "var(--error-text)",
                        minHeight: "44px",
                        display: "inline-flex",
                        alignItems: "center",
                        opacity: isResetting ? 0.6 : 1,
                      }}
                      title="Reset staging environment to fresh state"
                    >
                      {isResetting ? "resetting..." : "reset"}
                    </motion.button>
                  ) : null}
                </div>
              </header>

              <div className="app-shell__tabs" role="tablist">
                {navTabs.map((tab) => (
                  <NavLink
                    key={tab.id}
                    to={tab.id}
                    end={tab.id === "/app/dashboard"}
                    className={({ isActive }) =>
                      classNames("app-shell__tab", {
                        "app-shell__tab--active": isActive || isRouteActive(tab.id),
                      })
                    }
                  >
                    <span>{tab.label}</span>
                  </NavLink>
                ))}
              </div>

              {demo ? (
                <motion.div
                  initial={{ opacity: 0, y: -4 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mono small app-shell__demo-flag"
                >
                  Demo Mode — no messages are sent.
                </motion.div>
              ) : null}

              <main className="app-shell__main">
                <Outlet />
              </main>

              <Rail orientation="horizontal" className="app-shell__footer-rail" style={{ boxShadow: "0 0 12px rgba(255,43,79,0.65)" }} />

              {!isDesktop ? (
                <nav className="app-shell__bottom-nav mono" aria-label="Primary">
                  {navTabs.map((tab) => (
                    <NavLink
                      key={tab.id}
                      to={tab.id}
                      end={tab.id === "/app/dashboard"}
                      className={({ isActive }) =>
                        classNames("app-shell__bottom-tab", {
                          "app-shell__bottom-tab--active": isActive || isRouteActive(tab.id),
                        })
                      }
                    >
                      {tab.label}
                    </NavLink>
                  ))}
                </nav>
              ) : null}
            </div>

            {isDesktop && detailPane.open ? (
              <aside className="sidepane-desktop" aria-label={typeof detailPane.title === "string" ? detailPane.title : undefined}>
                <div id="app-shell-detail-pane-portal" style={{ display: "contents" }} />
              </aside>
            ) : null}
          </div>

          <Drawer
            open={navOpen}
            onClose={() => setNavOpen(false)}
            title={`${personaLabel} menu`}
            side="left"
          >
            <div className="drawer-nav" id="app-shell-nav">
              <nav aria-label="Primary">
                <span className="drawer-nav__group-title">Navigate</span>
                <ul>
                  {navTabs.map((tab) => (
                    <li key={tab.id}>
                      <button
                        type="button"
                        onClick={() => handleNavigate(tab.id)}
                        aria-current={isRouteActive(tab.id) ? "page" : undefined}
                      >
                        {tab.label}
                      </button>
                    </li>
                  ))}
                </ul>
              </nav>

              <div>
                <span className="drawer-nav__group-title">Quick actions</span>
                <div className="drawer-nav" style={{ gap: "8px" }}>
                  {tenant.payments_ready ? (
                    <button
                      type="button"
                      onClick={() => {
                        setNavOpen(false)
                        handleMessageClick()
                      }}
                    >
                      Message your {personaLabel.toLowerCase()}
                    </button>
                  ) : null}
                  <button type="button" onClick={toggleDemo}>
                    {demo ? "Exit demo mode" : "Enable demo mode"}
                  </button>
                  <ThemeToggle />
                  {IS_STAGING && ENABLE_ADMIN ? (
                    <button type="button" onClick={handleStagingReset} disabled={isResetting}>
                      {isResetting ? "Resetting staging…" : "Reset staging data"}
                    </button>
                  ) : null}
                </div>
              </div>
            </div>
          </Drawer>
        </div>
      </DetailPaneContext.Provider>
    </ControlCenterContext.Provider>
  )
}

function SceneWrapper({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        fontFamily: '"IBM Plex Mono","Space Mono",ui-monospace,Menlo,Consolas,monospace',
        letterSpacing: "0.12em",
        color: "var(--text, #fff)",
        background: "var(--bg, #0a0a0b)",
      }}
    >
      {children}
    </div>
  )
}

export default AppShell

type TabDefinition = {
  id: string
  label: string | Record<"demon" | "fairy", string>
}

const BASE_TABS: TabDefinition[] = [
  { id: "/app/dashboard", label: "Dashboard" },
  { id: "/app/invoices", label: "Invoices" },
  { id: "/app/customers", label: "Customers" },
  { id: "/app/billing", label: "Billing" },
  { id: "/app/settings", label: "Settings" },
]

function getTabsForTheme(theme: "demon" | "fairy") {
  const tabs = BASE_TABS.map((tab) => ({
    id: tab.id,
    label: typeof tab.label === "string" ? tab.label : tab.label[theme],
  }))

  if (ENABLE_ADMIN) {
    tabs.push({
      id: "/app/admin",
      label: "Admin",
    })
  }

  return tabs
}
